import React, { useMemo } from 'react';
import { Html } from '@react-three/drei';
import { useBuildings } from '../../features/warehouse/useBuildings';
import { useOccupancy } from '../../features/warehouse/useOccupancy';

// ─── CONSTANTS (same as Building3D.js) ─────────────────────
const BAY_LEN_M = 1.3;
const RACK_DEPTH_M = 1.0;
const RACK_LEVEL_H = 1.9;
const DRUM_CELL_W = 1.9;
const DRUM_CELL_D = 1.9;
const DRUM_ZONE_H = 1.0;

const SLOT_COLORS = {
  empty: '#6B7280',
  occupied: '#22C55E',
  reserved: '#3B82F6',
  blocked: '#EF4444'
};

function slotColor(entry) {
  if (!entry || entry.status === 'empty') return SLOT_COLORS.empty;
  if (entry.status === 'blocked') return SLOT_COLORS.blocked;
  if (entry.item?.status === 'reserved') return SLOT_COLORS.reserved;
  return SLOT_COLORS.occupied;
}

// ─── SLOT 3D ───────────────────────────────────────────────

function Slot3D({ worldX, worldY, worldZ, cw, cd, levelH, entry }) {
  const color = slotColor(entry);
  const isOccupied = entry?.status === 'occupied';
  const isBlocked = entry?.status === 'blocked';
  const isEmpty = !entry || entry.status === 'empty';

  return (
    <group position={[worldX, worldY, worldZ]}>
      <mesh position={[0, levelH / 2, 0]}>
        <boxGeometry args={[cw - 0.06, levelH - 0.05, cd - 0.06]} />
        {isEmpty ? (
          <meshBasicMaterial color="#9CA3AF" wireframe transparent opacity={0.6} />
        ) : (
          <meshStandardMaterial color={color} transparent opacity={isBlocked ? 0.65 : 0.4} />
        )}
      </mesh>
      {isEmpty && (
        <mesh position={[0, levelH / 2, 0]}>
          <boxGeometry args={[cw - 0.04, levelH - 0.03, cd - 0.04]} />
          <meshBasicMaterial color="#B0B8C4" wireframe transparent opacity={0.35} />
        </mesh>
      )}
      {isOccupied && entry.item && (
        <group>
          <mesh position={[0, 0.075, 0]} castShadow>
            <boxGeometry args={[cw * 0.85, 0.15, cd * 0.85]} />
            <meshStandardMaterial color="#8b5a2b" roughness={0.9} />
          </mesh>
          <mesh position={[0, 0.15 + (levelH * 0.3), 0]} castShadow>
            <boxGeometry args={[cw * 0.8, levelH * 0.6, cd * 0.8]} />
            <meshStandardMaterial color="#3a95c9" roughness={0.6} />
          </mesh>
        </group>
      )}
    </group>
  );
}

function RackLabel({ position, label }) {
  return (
    <Html position={position} center>
      <div style={{
        background: 'rgba(0,0,0,0.8)', color: '#FFF', padding: '3px 10px',
        borderRadius: 4, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        userSelect: 'none', pointerEvents: 'none', border: '1px solid #555'
      }}>
        {label}
      </div>
    </Html>
  );
}

function StatsOverlay({ position, name, occupancy }) {
  const stats = useMemo(() => {
    if (!occupancy) return { total: 0, occupied: 0, empty: 0, blocked: 0 };
    const entries = Object.values(occupancy);
    return {
      total: entries.length,
      occupied: entries.filter(e => e.status === 'occupied').length,
      empty: entries.filter(e => e.status === 'empty').length,
      blocked: entries.filter(e => e.status === 'blocked').length
    };
  }, [occupancy]);
  const pct = stats.total > 0 ? Math.round((stats.occupied / stats.total) * 100) : 0;

  return (
    <Html position={position} center>
      <div style={{
        background: 'rgba(10,20,30,0.92)', color: '#FFF', padding: '10px 16px',
        borderRadius: 8, fontSize: 11, fontFamily: 'monospace',
        border: '2px solid #555', userSelect: 'none', pointerEvents: 'none', minWidth: 160
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6, textAlign: 'center', borderBottom: '1px solid #444', paddingBottom: 4 }}>{name}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span>Occupancy</span>
          <span style={{ color: pct > 80 ? '#EF4444' : pct > 50 ? '#F59E0B' : '#22C55E', fontWeight: 700, fontSize: 13 }}>{pct}%</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span><span style={{ color: SLOT_COLORS.occupied }}>●</span> Occupied</span>
          <span style={{ fontWeight: 600 }}>{stats.occupied}/{stats.total}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span><span style={{ color: SLOT_COLORS.empty }}>●</span> Empty</span>
          <span>{stats.empty}</span>
        </div>
        {stats.blocked > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span><span style={{ color: SLOT_COLORS.blocked }}>●</span> Blocked</span>
            <span>{stats.blocked}</span>
          </div>
        )}
      </div>
    </Html>
  );
}

// ─── BUILD SLOTS — exact same logic as Building3D.js ───────
// Produces slots centered at origin, then the parent <group>
// positions them at the factory building center.

function buildSlots(layout, occupancy) {
  const out = [];
  const buildingCode = layout._buildingCode || 'B?';
  const offsetX = -layout.widthM / 2;
  const offsetZ = -layout.depthM / 2;

  for (const aisle of layout.aisles || []) {
    const isDrum = aisle.kind === 'drum-floor';
    const isVertical = aisle.orientation === 'vertical';
    const bayLen = isDrum ? DRUM_CELL_W : BAY_LEN_M;
    const rackDepth = isDrum ? DRUM_CELL_D : RACK_DEPTH_M;

    for (let bay = 1; bay <= aisle.bays; bay++) {
      for (let level = 1; level <= (aisle.levels || 1); level++) {
        const code = `${buildingCode}-${aisle.id}-${String(bay).padStart(2, '0')}-L${level}`;
        const i = bay - 1;
        const localX = isVertical
          ? aisle.x + rackDepth / 2
          : aisle.x + (i + 0.5) * bayLen;
        const localZ = isVertical
          ? aisle.y + (i + 0.5) * bayLen
          : aisle.y + rackDepth / 2;
        const cw = isVertical ? rackDepth : bayLen;
        const cd = isVertical ? bayLen : rackDepth;
        const y = isDrum ? 0 : (level - 1) * RACK_LEVEL_H;

        out.push({
          code,
          aisleId: aisle.id,
          bay, level, isDrum, cw, cd,
          levelH: isDrum ? DRUM_ZONE_H : RACK_LEVEL_H - 0.1,
          worldX: offsetX + localX,
          worldY: y + 0.22,
          worldZ: offsetZ + localZ,
          entry: occupancy?.[code]
        });
      }
    }
  }
  return out;
}

function getAisleLabels(layout) {
  const offsetX = -layout.widthM / 2;
  const offsetZ = -layout.depthM / 2;

  return (layout.aisles || []).map(a => {
    const isVert = a.orientation === 'vertical';
    const bayLen = a.kind === 'drum-floor' ? DRUM_CELL_W : BAY_LEN_M;
    const rackDepth = a.kind === 'drum-floor' ? DRUM_CELL_D : RACK_DEPTH_M;
    const midBay = (a.bays * bayLen) / 2;

    const lx = isVert ? a.x + rackDepth / 2 : a.x + midBay;
    const lz = isVert ? a.y + midBay : a.y + rackDepth / 2;

    return { id: a.id, x: offsetX + lx, z: offsetZ + lz };
  });
}

// ─── DEPOSIT INTERIOR ──────────────────────────────────────
// Uses exact same slot calculation as Building3D.js
// Centered at origin, then <group position> places it at the
// factory building center.

export function DepositInterior({ buildingConfig }) {
  const { id, xStart, xEnd, zStart, zEnd } = buildingConfig;
  const bCode = id === 'deposit-1' ? 'B1' : 'B2';

  const { data: buildings } = useBuildings();
  const building = buildings?.find(b => b.code === bCode);
  const { data: occData } = useOccupancy(building?._id);

  const layout = occData?.building?.layout || building?.layout;
  const occupancy = occData?.occupancy;

  const slots = useMemo(() => {
    if (!layout) return [];
    return buildSlots(layout, occupancy);
  }, [layout, occupancy]);

  const aisleLabels = useMemo(() => {
    if (!layout) return [];
    return getAisleLabels(layout);
  }, [layout]);

  if (!layout) return null;

  // Factory building center
  const cx = (xStart + xEnd) / 2;
  const cz = (zStart + zEnd) / 2;

  return (
    <group position={[cx, 0, cz]}>
      {slots.map(s => (
        <Slot3D
          key={s.code}
          worldX={s.worldX}
          worldY={s.worldY}
          worldZ={s.worldZ}
          cw={s.cw}
          cd={s.cd}
          levelH={s.levelH}
          entry={s.entry}
        />
      ))}

      {aisleLabels.map(a => (
        <RackLabel key={a.id} position={[a.x, 5.5, a.z]} label={a.id} />
      ))}

      <StatsOverlay position={[0, 7.5, 0]} name={bCode === 'B1' ? 'Deposit 1' : 'Deposit 2'} occupancy={occupancy} />
    </group>
  );
}
